import React, { useState } from 'react';
import { signUp, login, auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { Camera, Info, X, Film } from 'lucide-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { grey } from '@mui/material/colors';

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [handle, setHandle] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showHowItWorks, setShowHowItWorks] = useState(true);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState('22');
  const [showSignUpDetails, setShowSignUpDetails] = useState(false);
  const [showDurationInfo, setShowDurationInfo] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [supplementalImages, setSupplementalImages] = useState([]);
  const [supplementalPreviews, setSupplementalPreviews] = useState([]);
  const [showSupplemental, setShowSupplemental] = useState(true);
  const [showScenesModal, setShowScenesModal] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    // Only check episode details if signing up and not yet showing signup details
    if (isSignUp && !showSignUpDetails) {
      // Validate episode details
      if (!image || !description) {
        setError('Please add a photo and description for your pilot episode');
        return;
      }
      const words = description.trim().split(/\s+/);
      if (words.length > 26) {
        setError('Description must be 26 words or less');
        return;
      }
      // Show signup details form
      setShowSignUpDetails(true);
      return;
    }

    // For sign up, ensure that terms have been accepted
    if (isSignUp && !termsAccepted) {
      setError('Please read and accept the Terms & Privacy Policy.');
      return;
    }

    if (!email || !password || (isSignUp && (!name || !handle))) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      if (isSignUp) {
        // Create user account with pilot episode
        await signUp(email, password, name, handle, {
          image,
          description,
          duration,
          title: 'Pilot',
          date: selectedDate.toDate(),
          supplementalImages,
          // Add show information
          showName: `${name}'s Show`,
          showDescription: `${name}'s personal show on Speil`,
        });
      } else {
        await login(email, password);
      }
      
      navigate('/feed');
    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // When the checkbox is toggled, if not already accepted, open the modal.
  const handleCheckboxChange = () => {
    if (!termsAccepted) {
      setShowTermsModal(true);
    } else {
      setTermsAccepted(false);
    }
  };

  // Called when user confirms they have read the policies.
  const handleAgreeTerms = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      setError('Please enter your email address');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
      setError('');
    } catch (error) {
      setError('Failed to send reset email. Please check if the email is correct.');
    }
  };

  const HowItWorksScreen = ({ onClose }) => {
    return (
      <>
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          zIndex: 1999
        }} />

        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          width: 'calc(100% - 2rem)',
          maxWidth: '500px',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'transparent',
          zIndex: 2000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem',
          borderRadius: '1rem'
        }}>
          <img 
            src="/HowSpeilWorks.svg" 
            alt="How Speil Works"
            style={{
              width: 'calc(100% - 2rem)',
              objectFit: 'contain'
            }}
          />
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              bottom: '2rem',
              left: '50%',
              transform: 'translateX(-50%)',
              padding: '.5rem 3rem',
              backgroundColor: '#6600CC',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              width: 'calc(100% - 6rem)',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}
          >
            Got it
          </button>
        </div>
      </>
    );
  };

  const handleLearnMoreClick = (e) => {
    e.preventDefault();
    setShowHowItWorks(true);
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const handleImageClick = () => {
    document.getElementById('imageInput').click();
  };

  // Add new component for the info modal
  const DurationInfoModal = ({ onClose }) => {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: '#1f1f1f',
          padding: '2rem',
          borderRadius: '1rem',
          maxWidth: '500px',
          width: '90%',
          position: 'relative'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '1rem',
            fontSize: '1.2rem'
          }}>
            Episode Length
          </h3>
          <p style={{ 
            color: '#8E8E8E',
            lineHeight: '1.5',
            marginBottom: '1.5rem'
          }}>
            This isn't about how long you spent recording or filming. Think of it like a TV show:
            <br/><br/>
            <strong style={{color: 'white'}}>22 minutes:</strong> A regular day with one main story or event
            <br/><br/>
            <strong style={{color: 'white'}}>44 minutes:</strong> An eventful day with multiple stories or significant moments
          </p>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: '#6600CC',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}
          >
            Got it
          </button>
        </div>
      </div>
    );
  };

  const handleSupplementalImageChange = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 6 - supplementalImages.length;
    const newFiles = files.slice(0, remainingSlots);

    setSupplementalImages(prev => [...prev, ...newFiles]);
    
    const newPreviews = newFiles.map(file => URL.createObjectURL(file));
    setSupplementalPreviews(prev => [...prev, ...newPreviews]);
  };

  const handleRemoveSupplemental = (index) => {
    setSupplementalImages(prev => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
    
    setSupplementalPreviews(prev => {
      const newPreviews = [...prev];
      URL.revokeObjectURL(newPreviews[index]);
      newPreviews.splice(index, 1);
      return newPreviews;
    });
  };

  const ScenesModal = ({ onClose }) => {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}>
        <div style={{
          backgroundColor: '#6600CC',
          padding: '1.5rem',
          borderRadius: '1rem',
          maxWidth: '300px',
          width: '90%',
          position: 'relative'
        }}>
          <h3 style={{ 
            color: 'white', 
            marginBottom: '1rem',
            fontSize: '1.2rem', marginTop: '-.5rem'
          }}>
            Scenes
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 80px)',
            gridTemplateRows: 'repeat(2, 80px)',
            gap: '1.5rem 0.5rem',
            marginBottom: '1rem',
            justifyContent: 'center',
            alignContent: 'center',
            minHeight: '200px'
          }}>
            {[...Array(6)].map((_, index) => {
              const hasImage = supplementalPreviews[index];
              
              // Custom styles for each position
              const getCustomStyle = (idx) => {
                switch(idx) {
                  case 1:
                    return {
                      zIndex: 2,
                      height: '7rem',
                      alignSelf: 'center'
                    };
                  case 2:
                    return {
                      zIndex: 1,
                      alignSelf: 'center'
                    };
                  case 3:
                    return {
                      zIndex: 1,
                      height: '7rem',
                      alignSelf: 'center'
                    };
                  case 4:
                    return {
                      zIndex: 1,
                      alignSelf: 'center'
                    };
                  case 5:
                    return {
                      zIndex: 1,
                      height: '7rem',
                      alignSelf: 'center'
                    };
                  default:
                    return {
                      zIndex: 0,
                      alignSelf: 'center'
                    };
                }
              };

              const customStyle = getCustomStyle(index);

              return (
                <div
                  key={index}
                  onClick={() => !hasImage && document.getElementById('supplementalInput').click()}
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#131313',
                    border: '1px solid #404040',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    position: 'relative',
                    cursor: hasImage ? 'default' : 'pointer',
                    transition: 'transform 0.2s ease',
                    ...customStyle
                  }}
                >
                  {hasImage ? (
                    <>
                      <img
                        src={supplementalPreviews[index]}
                        alt={`Scene ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSupplemental(index);
                        }}
                        style={{
                          position: 'absolute',
                          top: '4px',
                          right: '4px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer'
                        }}
                      >
                        <X size={10} color="white" />
                      </button>
                    </>
                  ) : (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#8E8E8E',
                      fontSize: '0.8rem'
                    }}>
                      +
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '0.75rem',
              marginTop: '1rem',
              backgroundColor: 'rgb(255,255,255,.3)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer'
            }}
          >
            Done
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#1a1a1a',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      {showHowItWorks && <HowItWorksScreen onClose={() => setShowHowItWorks(false)} />}

      <div style={{
        width: '100%',
        maxWidth: '400px',
        padding: '2rem',
        backgroundColor: '#1f1f1f',
        borderRadius: '1rem',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
        border: '1px solid #333'
      }}>
        <h1 style={{
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          Welcome to Speil
        </h1>
        <p style={{
          marginLeft: 'auto',
          marginRight: 'auto',
          color: 'lightgrey',
          fontSize: '.8rem',
          textAlign: 'center'
        }}>
          Log life as if it were a TV show   <span 
              onClick={handleLearnMoreClick}
              style={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '.7rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Learn more...
            </span>
        </p>

        {isSignUp ? (
          !showSignUpDetails ? (
            // Episode Creation Form
            <form onSubmit={handleSubmit}>
              <div style={{    display: 'flex', 
                flexDirection: 'column', 
                gap: '1rem', 
                  background: '#131313', 
                  padding: '1.5rem', 
                borderRadius: '10px', 
                position: 'relative' 
                
                }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem'
                }}>
                  <span style={{ 
                      fontSize: '1.2rem', 
                      color: '#8E8E8E', 
                      fontWeight: '500'
                    }}>
                      1.
                    </span>
                  <div style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#1f1f1f',
                    border: '1px solid #404040',
                    borderRadius: '0.5rem',
                    color: 'grey',
                    fontSize: '1rem'
                  }}>
                    Pilot
                  </div>

                  
                </div>

             <div style={{display: 'flex'}}>
                <input
                  id="imageInput"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                
                <div
                  onClick={handleImageClick}
                  style={{
                    width: '120px',
                    height: '85px',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                    backgroundColor: imagePreview ? 'transparent' : '#1f1f1f',
                    overflow: 'hidden',
                    border: '1px solid #404040'
                  }}
                >
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Camera size={24} color="#8E8E8E" />
                  )}
                </div>
<div>
  
                <div style={{
                  display: 'flex', marginLeft: '1rem'
                }}>
                  <div style={{
                    display: 'flex', 
                    gap: '0.5rem', 
                      backgroundColor: '#1f1f1f',width: '100%',
                      padding: '0.25rem',
                      borderRadius: '0.5rem',
                      marginBottom: '0.5rem'
                  }}>
                    <button
                      type="button"
                      onClick={() => setDuration('22')}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: duration === '22' ? '#6600CC' : 'transparent',
                        color: duration === '22' ? 'white' : '#8E8E8E',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer'
                      }}
                    >
                      22m
                    </button>
                    <button
                      type="button"
                      onClick={() => setDuration('44')}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: duration === '44' ? '#6600CC' : 'transparent',
                        color: duration === '44' ? 'white' : '#8E8E8E',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer'
                      }}
                    >
                      44m
                    </button>
                  </div>



                  <button
                    type="button"
                    onClick={() => setShowDurationInfo(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: '0.5rem',
                      cursor: 'pointer', 
                      color: 'grey'
                    }}
                  >








                    <Info></Info>
                  </button>
                  </div>

                  <div style={{
                  marginBottom: '1rem'
                }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker 
                      value={selectedDate}
                      onChange={(newValue) => setSelectedDate(newValue)}
                      sx={{
                        width: 'calc(100% - 1rem)',
                        marginLeft: '1rem',
                        '& .MuiInputBase-root': {
                          color: 'white',
                          height: '40px',
                          backgroundColor: '#131313',
                          border: '1px solid #404040',
                          borderRadius: '0.5rem',
                          '&:hover': {
                            borderColor: '#6600CC',
                          },
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '& .MuiIconButton-root': {
                          color: '#8E8E8E'
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>


                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '.4rem',
                  background: '#1f1f1f',
                  marginTop: '-.8rem',
                  marginBottom: '.2rem',
                  padding: '.5rem 1rem',
                  cursor: 'pointer'
                }} onClick={() => {
                  if (supplementalImages.length > 0) {
                    setShowScenesModal(true);
                  } else {
                    document.getElementById('supplementalInput').click();
                  }
                }}>
                  <span style={{ 
                    color: 'white', 
                    fontSize: '0.9rem',
                    fontWeight: '500' 
                  }}>
                    Scenes (optional)
                 </span>
                       
                 <Film size={16} style={{marginTop: '4px '}} color="white" />
             
                  <div style={{ marginLeft: 'auto' }}>
                    {supplementalImages.length > 0 && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        color: 'white',
                        fontWeight: '600' 
                      }}>
                        {supplementalImages.length}
                      </span>
                    )}
                  </div>
                </div>

                <input
                  id="supplementalInput"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleSupplementalImageChange}
                  style={{ display: 'none' }}
                />

                <textarea
                  value={description}
                  onChange={(e) => {
                    const words = e.target.value.trim().split(/\s+/);
                    if (words.length <= 26 || e.target.value.length < description.length) {
                      setDescription(e.target.value);
                    }
                  }}
                  placeholder="Episode description - ex. Jeremy faces unexpected challenges when a surprise birthday party threatens to derail his carefully planned presentation."
                  style={{
                    width: 'calc(100% - 2rem + 4px)',
                    padding: '0.75rem',
                    backgroundColor: '#131313',
                    border: '1px solid #404040',
                    borderRadius: '0.5rem',
                    color: 'white',
                    minHeight: '80px',
                    resize: 'none',
                    marginBottom: '0.5rem',
                    outline: ' grey',
                    fontSize: '1rem',
                    overflowY: 'hidden',
                    overflowX: 'hidden'
                  }}
                />

                <div style={{
                  fontSize: '0.75rem',
                  color: description.trim().split(/\s+/).length > 26 ? '#ef4444' : '#8E8E8E',
                  marginTop: '-2rem',
                  textAlign: 'right'
                }}>
                  {description.trim().split(/\s+/).length}/26 words
                </div>

                <button
                  type="submit"
                  disabled={!image || !description.trim()}
                  style={{
                    background: !image || !description.trim() 
                      ? '#6600CC80' 
                      : 'linear-gradient(90deg, #6600CC 0%, #7c3aed 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.75rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: !image || !description.trim() ? 'not-allowed' : 'pointer',
                    width: '100%'
                  }}
                >
                  Create First Episode
                </button>
              </div>
            </form>
          ) : (
            // Sign Up Details Form
            <form onSubmit={handleSubmit}>
              <div 
                 onClick={() => setShowSignUpDetails(false)}
                 
                 style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                background: 'transparent',
                padding: ' 0rem 1.5rem ',
                position: 'relative',
                marginBottom: '1.5rem'
              }}>
              
                  <span style={{
                     position: 'absolute',
                     top: '20px',
                     left: '0px',
                     width: '20px',
                     textAlign: 'center'

                  }}>
                    1.
                  </span>
                 

                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{
                    width: '80px',
                    height: '65px',
                    borderRadius: '0.5rem',
                    overflow: 'hidden',
                    border: '1px solid #404040'
                  }}>
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="Episode"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>

<div style={{
              fontSize: '1rem',
              color: 'white',
              fontWeight: '600',
              paddingBottom: '.2rem',
              cursor: 'pointer'
                  }}>
                    Pilot
                  </div>

                    
                      <div style={{
                        color: '#8E8E8E',
                        fontSize: '0.7rem'
                      }}>
                        {selectedDate.format('MMM D, YYYY')}
                      </div>
                      <div style={{
                        marginLeft: 'auto',
                        color: '#8E8E8E',
                        fontSize: '0.6rem'
                      }}>
                        {duration}m
                      </div>

                    </div>
                    <p style={{
                      color: 'white',
                      fontSize: '0.5rem',
                      lineHeight: '1.4'
                    }}>
                      {description}
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem', marginRight: '25px' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#131313',
                    border: '1px solid #404040',
                    borderRadius: '0.5rem',
                    color: 'white',
                    outline: 'none',
                    marginBottom: '1rem'
                  }}
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: '#131313',
                    border: '1px solid #404040',
                    borderRadius: '0.5rem',
                    color: 'white',
                    outline: 'none',
                    marginBottom: isSignUp ? '1rem' : 0
                  }}
                />
                {isSignUp && (
                  <>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Name"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        backgroundColor: '#131313',
                        border: '1px solid #404040',
                        borderRadius: '0.5rem',
                        color: 'white',
                        outline: 'none',
                        marginBottom: '1rem'
                      }}
                    />
                    <input
                      type="text"
                      value={handle}
                      onChange={(e) => setHandle(e.target.value)}
                      placeholder="Handle (without @)"
                      style={{
                        width: '100%',
                        padding: '0.75rem',
                        backgroundColor: '#131313',
                        border: '1px solid #404040',
                        borderRadius: '0.5rem',
                        color: 'white',
                        outline: 'none'
                      }}
                    />
                  </>
                )}
              </div>

              {isSignUp && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={handleCheckboxChange}
                  />
                  <label
                    style={{
                      color: 'white',
                      marginLeft: '0.5rem',
                      cursor: 'pointer'
                    }}
                    onClick={() => setShowTermsModal(true)}
                  >
                    I agree to the Terms & Privacy Policy and that I am over 16
                  </label>
                </div>
              )}

              {!isSignUp && (
                <div style={{
                  textAlign: 'right',
                  marginTop: '-0.5rem',
                  marginBottom: '1rem'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6600CC',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      padding: 0
                    }}
                  >
                    Forgot Password?
                  </button>
                </div>
              )}

              {error && (
                <div style={{
                  color: '#ef4444',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (isSignUp && !termsAccepted)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: isLoading || (isSignUp && !termsAccepted) ? '#6600CC80' : '#6600CC',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: isLoading || (isSignUp && !termsAccepted) ? 'not-allowed' : 'pointer',
                  fontWeight: '500',
                  fontSize: '1rem'
                }}
              >
                {isLoading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
              </button>
            </form>
          )
        ) : (
          // Sign In Form
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.5rem', marginRight: '25px' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: '#131313',
                  border: '1px solid #404040',
                  borderRadius: '0.5rem',
                  color: 'white',
                  outline: 'none',
                  marginBottom: '1rem'
                }}
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: '#131313',
                  border: '1px solid #404040',
                  borderRadius: '0.5rem',
                  color: 'white',
                  outline: 'none',
                  marginBottom: isSignUp ? '1rem' : 0
                }}
              />
            </div>

            {error && (
              <div style={{
                color: '#ef4444',
                marginBottom: '1rem',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || (isSignUp && !termsAccepted)}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: isLoading || (isSignUp && !termsAccepted) ? '#6600CC80' : '#6600CC',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: isLoading || (isSignUp && !termsAccepted) ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '1rem'
              }}
            >
              {isLoading ? 'Loading...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </button>
          </form>
        )}

        {/* Add sign in/up link at bottom */}
        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center',
          color: '#8E8E8E',
          fontSize: '0.9rem'
        }}>
          {isSignUp ? (
            <>
              Already have an account?{' '}
              <button
                onClick={() => setIsSignUp(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6600CC',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                  textDecoration: 'underline'
                }}
              >
                Sign in
              </button>
            </>
          ) : (
            <>
              Don't have an account?{' '}
              <button
                onClick={() => setIsSignUp(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6600CC',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                  textDecoration: 'underline'
                }}
              >
                Sign up
              </button>
            </>
          )}
        </div>
      </div>

      {/* Modal for Terms & Privacy Policy */}
      {showTermsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f1f1f',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '80%',
            overflowY: 'auto',
            borderRadius: '1rem',
            padding: '2rem',
            position: 'relative'
          }}>
            <h2 style={{ color: 'white', marginBottom: '1rem' }}>Privacy Policy</h2>
            <p style={{ color: 'lightgrey', fontSize: '0.875rem' }}>
              Effective Date: 03/01/25<br/>
              Last Updated: 03/01/25<br/><br/>
              This Privacy Policy explains how 4FRNT Education LLC ("we," "our," or "us") collects, uses, and discloses information about you when you use our web application Speil (the "App") available at https://speil-tdy.web.app/. By accessing or using our App, you agree to the collection and use of information in accordance with this Privacy Policy.<br/><br/>
              <strong>1. Information We Collect</strong><br/>
              a. Personal Information<br/>
              Account Registration: When you register for an account, we may collect your name, email address, and any other information you choose to provide.<br/>
              Support Communications: If you contact our support team via email (rodrigo@onda-st.com), we will keep a record of that correspondence.<br/><br/>
              b. User-Generated Content<br/>
              Images and Diary Entries: Speil allows you to upload images and create diary entries that are structured similarly to a Netflix show. Any content you upload is subject to this policy.<br/>
              Friend Connections: You may add up to 10 friends within the App. Information associated with these connections, such as usernames or profiles, may be stored and shared within your network as part of the App's features.<br/><br/>
              c. Usage Data<br/>
              We automatically collect usage data that may include log data, device information, browser type, access times, and interactions with our App.<br/><br/>
              <strong>2. How We Use Your Information</strong><br/>
              We use the information we collect for purposes including providing and maintaining our service, account management, communication, and personalization.<br/><br/>
              <strong>3. Sharing Your Information</strong><br/>
              Within the App: Your diary entries, images, and friend connection data may be visible to others within your network.<br/>
              Third-Party Service Providers: We may share your information with trusted service providers who help operate the App.<br/><br/>
              <strong>4. Data Security</strong><br/>
              We take reasonable measures to protect your information, though no transmission method over the Internet is completely secure.<br/><br/>
              <strong>5. Data Breach Notification</strong><br/>
              In the event of a data breach, we will notify you in accordance with the Florida Information Protection Act (FIPA) and other applicable laws.<br/><br/>
              <strong>6. Data Retention and Deletion</strong><br/>
              Retention: We retain your information only as long as necessary to fulfill the purposes of this Privacy Policy.<br/>
              Deletion: You may request deletion of your account and associated data by contacting us at rodrigo@onda-st.com.<br/><br/>
              <strong>7. Cookies and Tracking Technologies</strong><br/>
              We may use cookies and similar technologies to track activity on our App. Refusing cookies may affect functionality.<br/><br/>
              <strong>8. Changes to This Privacy Policy</strong><br/>
              We may update this Privacy Policy periodically. Your continued use of the App will constitute acceptance of the updated policy.<br/><br/>
              <strong>9. Contact Us</strong><br/>
              Email: rodrigo@onda-st.com
            </p>

            <h2 style={{ color: 'white', margin: '1.5rem 0 1rem' }}>Terms of Service</h2>
            <p style={{ color: 'lightgrey', fontSize: '0.875rem' }}>
              Effective Date: 03/01/25<br/><br/>
              Please read these Terms of Service ("Terms") carefully before using the Speil web application (the "Service") operated by 4FRNT Education LLC. By accessing or using the Service, you agree to be bound by these Terms.<br/><br/>
              <strong>1. Acceptance of Terms</strong><br/>
              By using the Service, you affirm that you are over 16 and agree to these Terms. If you do not agree, please do not use the Service.<br/><br/>
              <strong>2. Description of the Service</strong><br/>
              Speil allows users to upload images, create diary entries in a show-like format, and connect with up to 10 friends.<br/><br/>
              <strong>3. Account Registration</strong><br/>
              You must create an account to access the Service and provide accurate, current information.<br/><br/>
              <strong>4. User Content</strong><br/>
              You retain ownership of your content, but grant us a license to use it for operating and improving the Service.<br/><br/>
              <strong>5. Friend Connections</strong><br/>
              Users may add up to 10 friends. Certain profile information may be visible to added friends per our Privacy Policy.<br/><br/>
              <strong>6. Prohibited Conduct</strong><br/>
              You agree not to use the Service for any illegal, harmful, or unauthorized purposes.<br/><br/>
              <strong>7. Intellectual Property</strong><br/>
              All intellectual property rights in the Service are owned by or licensed to 4FRNT Education LLC.<br/><br/>
              <strong>8. Termination</strong><br/>
              We reserve the right to suspend or terminate your account if you violate these Terms.<br/><br/>
              <strong>9. Disclaimers and Limitation of Liability</strong><br/>
              The Service is provided "as is" without warranties, and 4FRNT Education LLC is not liable for any indirect damages.<br/><br/>
              <strong>10. Governing Law</strong><br/>
              These Terms are governed by the laws of the State of Florida.<br/><br/>
              <strong>11. Changes to Terms</strong><br/>
              We reserve the right to modify these Terms at any time. Continued use of the Service constitutes acceptance of any changes.<br/><br/>
              <strong>12. Contact Us</strong><br/>
              Email: rodrigo@onda-st.com
            </p>

            <button
              onClick={handleAgreeTerms}
              style={{
                marginTop: '1.5rem',
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#6600CC',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '1rem'
              }}
            >
              I've read and agree
            </button>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1f1f1f',
            padding: '2rem',
            borderRadius: '1rem',
            width: '90%',
            maxWidth: '400px'
          }}>
            <h2 style={{
              color: 'white',
              marginBottom: '1rem'
            }}>
              Reset Password
            </h2>
            <form onSubmit={handleForgotPassword}>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your email"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: '#131313',
                  border: '1px solid #404040',
                  borderRadius: '0.5rem',
                  color: 'white',
                  marginBottom: '1rem'
                }}
              />
              {resetMessage && (
                <div style={{
                  color: '#10B981',
                  marginBottom: '1rem',
                  fontSize: '0.875rem'
                }}>
                  {resetMessage}
                </div>
              )}
              <div style={{
                display: 'flex',
                gap: '1rem',
                marginTop: '1rem'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail('');
                    setResetMessage('');
                    setError('');
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#131313',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    backgroundColor: '#6600CC',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    cursor: 'pointer'
                  }}
                >
                  Send Reset Link
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duration Info Modal */}
      {showDurationInfo && (
        <DurationInfoModal onClose={() => setShowDurationInfo(false)} />
      )}

      {/* Scenes Modal */}
      {showScenesModal && (
        <ScenesModal onClose={() => setShowScenesModal(false)} />
      )}
    </div>
  );
};

export default SignUp;
